import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  cancelSkillAgentSearch,
  clearSkillAgentSearchPhotos,
  deleteSkillAgentSearch,
  deleteSkillAgentSearchItems,
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  getSkillAgentSearchesMeta,
  listSkillAgentSearchItems,
  listSkillAgentSearchRuns,
  rerunSkillAgentSearchRun,
  runSkillAgentSearch,
  updateSkillAgentSearch,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { searchStatusLabel } from "./skillAgentTaskTree";
import SkillAgentBrandSourcesFields, {
  buildDefaultUrlSelection,
  healUrlSelection,
} from "./skillAgentBrandSourcesFields";
import SkillAgentCustomSourcesFields, {
  customSourcesFromLegacy,
  customSourcesToUrls,
  normalizeCustomSources,
} from "./skillAgentCustomSourcesFields";
import SkillAgentResearchCatalogDialog from "./SkillAgentResearchCatalogDialog";

function SearchThumb({ item, onOpen, selected, onToggleSelect, selectable }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => src && onOpen?.(item)}
      sx={{
        position: "relative",
        borderRadius: 2.25,
        overflow: "hidden",
        border: "1px solid",
        borderColor: selected ? "error.main" : alpha("#0f172a", 0.08),
        bgcolor: "background.paper",
        cursor: src ? "zoom-in" : "default",
        transition:
          "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": src
          ? {
              borderColor: selected ? "error.main" : "primary.main",
              boxShadow: (t) =>
                `0 8px 22px ${alpha(t.palette.primary.main, 0.16)}`,
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      {selectable ? (
        <Checkbox
          size="small"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect?.(item, e.target.checked);
          }}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.9),
            borderRadius: 1,
            boxShadow: `0 1px 4px ${alpha("#0f172a", 0.12)}`,
            "&:hover": { bgcolor: "#fff" },
          }}
          inputProps={{
            "aria-label": t("skillAgent.search.ariaSelectPhoto"),
          }}
        />
      ) : null}
      <Box
        sx={{
          height: 168,
          bgcolor: alpha("#0f172a", 0.03),
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={() => setFailed(true)}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary">
            {t("skillAgent.common.noPreview")}
          </Typography>
        ) : (
          <CircularProgress size={18} />
        )}
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.7,
          borderTop: "1px solid",
          borderColor: alpha("#0f172a", 0.06),
          bgcolor: alpha("#fff", 0.92),
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          display="block"
          sx={{ letterSpacing: 0.01 }}
        >
          {item.Title || item.BrandName || "img"}
        </Typography>
      </Box>
    </Box>
  );
}

function SearchPhotoLightbox({
  items,
  index,
  onClose,
  onIndexChange,
  onDeleteCurrent,
  deleting,
}) {
  const { t } = useTranslation();
  const item = index != null && items[index] ? items[index] : null;
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.id) {
      setSrc("");
      return undefined;
    }
    let revoked = false;
    let objectUrl = "";
    setSrc("");
    setFailed(false);
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item?.id]);

  useEffect(() => {
    if (item == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onIndexChange?.(Math.max(0, index - 1));
      if (e.key === "ArrowRight")
        onIndexChange?.(Math.min(items.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, index, items.length, onClose, onIndexChange]);

  if (!item) return null;

  const driveHref = item.DriveFileUrl || null;
  const sourceHref = item.SourceUrl || null;
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {item.Title || item.BrandName || t("skillAgent.search.lightboxPhoto")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[item.BrandName, `${index + 1} / ${items.length}`]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        {sourceHref ? (
          <Button
            size="small"
            component={Link}
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.search.openSource")}
          </Button>
        ) : null}
        {driveHref ? (
          <Button
            size="small"
            component={Link}
            href={driveHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.common.drive")}
          </Button>
        ) : null}
        {onDeleteCurrent ? (
          <Button
            size="small"
            color="error"
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={() => onDeleteCurrent(item)}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.common.delete")}
          </Button>
        ) : null}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("skillAgent.common.close")}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
          bgcolor: alpha("#000", 0.92),
          minHeight: { xs: 320, md: 560 },
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "70vh", md: "78vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : failed ? (
          <Typography color="grey.400">
            {t("skillAgent.search.lightboxLoadError")}
          </Typography>
        ) : (
          <CircularProgress sx={{ color: "grey.200" }} />
        )}
        <IconButton
          disabled={!canPrev}
          onClick={() => onIndexChange?.(index - 1)}
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label={t("skillAgent.search.lightboxPrev")}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          disabled={!canNext}
          onClick={() => onIndexChange?.(index + 1)}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label={t("skillAgent.search.lightboxNext")}
        >
          <ChevronRightIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
}

export default function SkillAgentSearchDetail({
  searchId,
  onChanged,
  onDeleted,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState(null);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [selectedDeleteOpen, setSelectedDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [editForm, setEditForm] = useState({
    Titulo: "",
    Tipologia: "",
    brandIds: [],
    urlSelection: {},
    customSources: [],
    maxImagesPerBrand: "3",
    maxAgeDays: "60",
  });
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [showSourceDetail, setShowSourceDetail] = useState(false);
  const [showRuns, setShowRuns] = useState(false);

  const images = useMemo(
    () => (Array.isArray(items) ? items.filter((i) => i.Kind === "image") : []),
    [items],
  );

  const runStats = useMemo(() => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : [];
    const customSources =
      Array.isArray(c.customSources) && c.customSources.length
        ? normalizeCustomSources(c.customSources).filter((s) => s.enabled)
        : customSourcesFromLegacy(c.customUrls);
    const perBrand = c.maxImagesPerBrand || 3;
    const ageDays = c.maxAgeDays != null ? c.maxAgeDays : 60;
    const nSources = brands.length + customSources.length;
    const latest = Array.isArray(runs) && runs[0] ? runs[0] : null;
    const statuses =
      latest?.SummaryJson && Array.isArray(latest.SummaryJson.sourceStatuses)
        ? latest.SummaryJson.sourceStatuses
        : [];
    const withHits = statuses.filter((s) => (s.passed || 0) > 0);
    const withErr = statuses.filter(
      (s) => s.status === "error" || s.status === "no_scrapeable",
    );
    const skippedMatch = String(search?.ResultSummary || "").match(
      /(\d+)\s*ya bajadas/,
    );
    const addedMatch = String(search?.ResultSummary || "").match(
      /\+(\d+)\s*fotos/,
    );
    return {
      nSources,
      ageDays,
      perBrand,
      statuses,
      withHits,
      withErr,
      skipped: skippedMatch ? skippedMatch[1] : null,
      added: addedMatch ? addedMatch[1] : null,
    };
  }, [search, runs]);

  const selectedCount = selectedIds.size;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchId]);

  useEffect(() => {
    const valid = new Set(images.map((i) => Number(i.id)));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [images]);

  const toggleSelect = useCallback((item, on) => {
    const id = Number(item.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selectAllPhotos = () => {
    setSelectedIds(new Set(images.map((i) => Number(i.id))));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const load = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSkillAgentSearchById(searchId);
      setSearch(data);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setRunning(data?.Status === "running");
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.loadError"));
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  const loadRuns = useCallback(async () => {
    if (!searchId) return;
    setRunsLoading(true);
    try {
      const data = await listSkillAgentSearchRuns(searchId);
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    const customerId = search?.CustomerId;
    getSkillAgentSearchesMeta(
      customerId ? { customerId: Number(customerId) } : {},
    )
      .then((m) => {
        const list = Array.isArray(m?.brands) ? m.brands : [];
        setCatalogBrands(
          [...list].sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
        );
      })
      .catch(() => setCatalogBrands([]));
  }, [search?.CustomerId]);

  useEffect(() => {
    if (!searchId || !running) return undefined;
    const pollTimer = setInterval(async () => {
      try {
        const data = await listSkillAgentSearchItems(searchId);
        if (data?.status && data.status !== "running") {
          setRunning(false);
          await load();
          await loadRuns();
          onChanged?.();
          if (data.status === "done") {
            const summary = String(data.resultSummary || "");
            const added = summary.match(/\+(\d+)\s*fotos/);
            const skipped = summary.match(/(\d+)\s*ya bajadas/);
            setOkMsg(
              t("skillAgent.search.pollDoneMsg", {
                n: added ? added[1] : 0,
                skipped: skipped ? skipped[1] : 0,
              }),
            );
          } else if (data.status === "error") {
            setError(
              String(data.resultSummary || t("skillAgent.search.pollError")).slice(
                0,
                180,
              ),
            );
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(pollTimer);
  }, [searchId, running, load, loadRuns, onChanged, t]);

  const openEdit = () => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : null;
    // [] explícito = sin marcas GU (solo webs propias). No volver a marcar todas.
    const brandIds = brands != null ? brands : catalogBrands.map((b) => b.id);
    const customSources =
      Array.isArray(c.customSources) && c.customSources.length
        ? normalizeCustomSources(c.customSources)
        : customSourcesFromLegacy(c.customUrls);
    const urlSelectionRaw =
      c.brandUrlSelection && typeof c.brandUrlSelection === "object"
        ? c.brandUrlSelection
        : buildDefaultUrlSelection(catalogBrands, brandIds);
    const urlSelection = healUrlSelection(
      urlSelectionRaw,
      catalogBrands,
      brandIds,
    );
    setEditForm({
      Titulo: search?.Titulo || "",
      Tipologia: search?.Tipologia || c.tipología || "",
      brandIds,
      urlSelection,
      customSources,
      maxImagesPerBrand: String(c.maxImagesPerBrand || 3),
      maxAgeDays: String(c.maxAgeDays != null ? c.maxAgeDays : 60),
    });
    setEditOpen(true);
  };

  const handleSaveParams = async ({ andRun = false } = {}) => {
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const tipology = String(editForm.Tipologia || "").trim();
      if (!tipology) {
        setError(t("skillAgent.create.tipologyRequired"));
        setBusy(false);
        return;
      }
      const customSources = normalizeCustomSources(editForm.customSources);
      const customUrls = customSourcesToUrls(customSources);
      const researchBrands = Array.isArray(editForm.brandIds)
        ? editForm.brandIds
        : [];
      if (
        !researchBrands.length &&
        !customSources.filter((s) => s.enabled).length
      ) {
        setError(t("skillAgent.search.needBrandOrUrl"));
        setBusy(false);
        return;
      }
      const prev =
        search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
          ? search.ConstraintsJson
          : {};
      const updated = await updateSkillAgentSearch(searchId, {
        Titulo: editForm.Titulo.trim() || search.Titulo,
        Tipologia: tipology,
        ConstraintsJson: {
          ...prev,
          tipología: tipology,
          researchBrands,
          brandUrlSelection: healUrlSelection(
            editForm.urlSelection || {},
            catalogBrands,
            researchBrands,
          ),
          customSources,
          customUrls,
          maxImagesPerBrand: Number(editForm.maxImagesPerBrand) || 3,
          maxAgeDays: Number(editForm.maxAgeDays) || 60,
          preferNewest: prev.preferNewest !== false,
          skipKnown: prev.skipKnown !== false,
          skipKnownScope: "clienteTemporada",
        },
      });
      setSearch((p) => ({ ...p, ...updated }));
      setEditOpen(false);
      setOkMsg(t("skillAgent.search.paramsSaved"));
      onChanged?.();
      if (andRun) {
        await runSkillAgentSearch(searchId, {
          customerFolderName: search?.CustomerName || undefined,
        });
        setRunning(true);
        setSearch((p) => (p ? { ...p, Status: "running" } : p));
        setOkMsg(t("skillAgent.search.rerunNewParams"));
      }
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const handleRerun = async (run) => {
    setError("");
    setOkMsg("");
    try {
      await rerunSkillAgentSearchRun(searchId, run.id, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      setOkMsg(t("skillAgent.search.rerunSameParams"));
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.runError"));
    }
  };

  const handleRun = async () => {
    setError("");
    setOkMsg("");
    try {
      await runSkillAgentSearch(searchId, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.runError"));
    }
  };

  const handleCancel = async () => {
    try {
      await cancelSkillAgentSearch(searchId);
      setRunning(false);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.cancelError"));
    }
  };

  const handleClearPhotos = async () => {
    setBusy(true);
    setError("");
    try {
      await clearSkillAgentSearchPhotos(searchId);
      setClearOpen(false);
      setSelectedIds(new Set());
      await load();
      onChanged?.();
      setOkMsg(t("skillAgent.search.photosCleared"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.deletePhotosError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const applyDeletedItems = (data, deletedIds) => {
    if (Array.isArray(data?.items)) {
      setItems(data.items);
    } else {
      const gone = new Set(deletedIds.map(Number));
      setItems((prev) => prev.filter((i) => !gone.has(Number(i.id))));
    }
    if (data?.search) setSearch(data.search);
    setSelectedIds((prev) => {
      const gone = new Set(deletedIds.map(Number));
      return new Set([...prev].filter((id) => !gone.has(id)));
    });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await deleteSkillAgentSearchItems(searchId, ids);
      setSelectedDeleteOpen(false);
      applyDeletedItems(data, ids);
      onChanged?.();
      setOkMsg(
        t("skillAgent.search.photosDeleted", {
          n: data?.deleted ?? ids.length,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.search.deleteSelectedPhotosError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLightboxItem = async (item) => {
    if (!item?.id) return;
    setBusy(true);
    setError("");
    try {
      const id = Number(item.id);
      const data = await deleteSkillAgentSearchItems(searchId, [id]);
      const remaining = Array.isArray(data?.items)
        ? data.items.filter((i) => i.Kind === "image")
        : images.filter((i) => Number(i.id) !== id);
      applyDeletedItems(data, [id]);
      onChanged?.();
      if (!remaining.length) {
        setLightboxIndex(null);
      } else {
        const nextIdx = Math.min(lightboxIndex ?? 0, remaining.length - 1);
        setLightboxIndex(nextIdx);
      }
      setOkMsg(t("skillAgent.search.photoDeleted"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.deletePhotosError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await deleteSkillAgentSearch(searchId);
      setDeleteOpen(false);
      onDeleted?.(searchId);
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.deleteError"));
      setBusy(false);
    }
  };

  if (loading && !search) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!search) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t("skillAgent.search.notFound")}
      </Alert>
    );
  }

  const locked = running || busy;
  const tipologiaShort = search.Tipologia
    ? String(search.Tipologia).length > 48
      ? `${String(search.Tipologia).slice(0, 48)}…`
      : search.Tipologia
    : null;
  const metaChipSx = {
    height: 24,
    borderRadius: 1.25,
    fontWeight: 700,
    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
    borderColor: (t) => alpha(t.palette.primary.main, 0.14),
    "& .MuiChip-label": { px: 0.9, fontSize: "0.72rem" },
  };
  const toolBtnSx = {
    width: 34,
    height: 34,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: alpha("#fff", 0.7),
    "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        p: { xs: 1.5, md: 2 },
        backgroundImage: (t) =>
          `radial-gradient(900px 280px at 0% -30%, ${alpha(t.palette.primary.main, 0.07)}, transparent)`,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 0.85,
          px: { xs: 0.75, md: 1 },
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.primary.main, 0.12),
          bgcolor: (t) => alpha(t.palette.background.paper, 0.88),
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow: `0 6px 20px ${alpha("#0f172a", 0.04)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Typography
            variant="h6"
            fontWeight={800}
            noWrap
            sx={{ lineHeight: 1.2, fontSize: { xs: "1.05rem", md: "1.15rem" } }}
          >
            {search.Titulo}
          </Typography>
          <Chip
            size="small"
            label={searchStatusLabel(search.Status)}
            color={
              search.Status === "done"
                ? "success"
                : search.Status === "error"
                  ? "error"
                  : search.Status === "running"
                    ? "warning"
                    : "default"
            }
            sx={{
              height: 22,
              borderRadius: 1.25,
              fontWeight: 800,
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.85, fontSize: "0.7rem" },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {search.DriveFolderUrl ? (
            <Tooltip title={t("skillAgent.search.openDriveFolder")}>
              <IconButton
                size="small"
                component="a"
                href={search.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={toolBtnSx}
              >
                <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={t("skillAgent.search.paramsTooltip")}>
            <span>
              <IconButton
                size="small"
                disabled={locked}
                onClick={openEdit}
                sx={toolBtnSx}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {running ? (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<StopOutlinedIcon />}
              onClick={handleCancel}
              sx={{
                height: 34,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 1.25,
              }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={handleRun}
              disabled={locked}
              sx={{
                height: 34,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 1.5,
                boxShadow: (theme) =>
                  `0 2px 10px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              {images.length
                ? t("skillAgent.search.rerun")
                : t("skillAgent.search.run")}
            </Button>
          )}
          <Tooltip title={t("skillAgent.search.deleteTooltip")}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={locked}
                onClick={() => setDeleteOpen(true)}
                sx={{
                  ...toolBtnSx,
                  borderColor: (t) => alpha(t.palette.error.main, 0.28),
                  color: "error.main",
                  "&:hover": {
                    bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ py: 0.5 }}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" onClose={() => setOkMsg("")} sx={{ py: 0.5 }}>
          {okMsg}
        </Alert>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.6}
        flexWrap="wrap"
        useFlexGap
      >
        {search.CustomerName ? (
          <Chip
            size="small"
            variant="outlined"
            label={search.CustomerName}
            sx={metaChipSx}
          />
        ) : null}
        {search.Temporada ? (
          <Chip
            size="small"
            variant="outlined"
            label={search.Temporada}
            sx={metaChipSx}
          />
        ) : null}
        {tipologiaShort ? (
          <Tooltip title={String(search.Tipologia || "")}>
            <Chip
              size="small"
              variant="outlined"
              label={tipologiaShort}
              sx={metaChipSx}
            />
          </Tooltip>
        ) : null}
        {runStats.nSources > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("skillAgent.search.chipSources", {
              n: runStats.nSources,
            })}
            sx={metaChipSx}
          />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={t("skillAgent.search.chipMaxAge", { d: runStats.ageDays })}
          sx={metaChipSx}
        />
        <Chip
          size="small"
          variant="outlined"
          label={t("skillAgent.search.chipPerBrand", {
            n: runStats.perBrand,
          })}
          sx={metaChipSx}
        />
        {runStats.added ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={t("skillAgent.search.chipAdded", { n: runStats.added })}
            sx={{
              ...metaChipSx,
              bgcolor: (t) => alpha(t.palette.success.main, 0.08),
            }}
          />
        ) : null}
        {runStats.skipped ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("skillAgent.search.chipSkipped", { n: runStats.skipped })}
            sx={metaChipSx}
          />
        ) : null}
        <Tooltip title={t("skillAgent.search.tooltipNoDuplicateDrive")}>
          <Chip
            size="small"
            variant="outlined"
            icon={<InfoOutlinedIcon sx={{ fontSize: "14px !important" }} />}
            label={t("skillAgent.search.chipNoDuplicateDrive")}
            sx={{
              ...metaChipSx,
              bgcolor: (t) => alpha(t.palette.info.main, 0.06),
              borderColor: (t) => alpha(t.palette.info.main, 0.22),
              "& .MuiChip-icon": { ml: 0.6, color: "info.main" },
            }}
          />
        </Tooltip>
      </Stack>

      {running && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: 1.25,
            py: 0.85,
            borderRadius: 2,
            border: "1px solid",
            borderColor: (t) => alpha(t.palette.warning.main, 0.25),
            bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
          }}
        >
          <CircularProgress size={16} color="warning" />
          <Typography variant="body2" fontWeight={600}>
            {t("skillAgent.search.runningBanner")}
          </Typography>
        </Stack>
      )}

      {(runStats.statuses.length > 0 || runs.length > 0) && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2.25,
            overflow: "hidden",
            bgcolor: alpha("#fff", 0.55),
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              const next = !(showSourceDetail || showRuns);
              setShowSourceDetail(next);
              setShowRuns(next);
            }}
            endIcon={
              showSourceDetail || showRuns ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            }
            startIcon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              justifyContent: "space-between",
              textTransform: "none",
              fontWeight: 700,
              color: "text.secondary",
              px: 1.35,
              py: 0.7,
              borderRadius: 0,
              minHeight: 36,
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Run · {runStats.withHits.length} con fotos
              {runStats.withErr.length
                ? ` · ${runStats.withErr.length} error/SPA`
                : ""}
              {runs.length ? ` · historial ${runs.length}` : ""}
            </Box>
          </Button>
          <Collapse in={showSourceDetail || showRuns}>
            <Stack spacing={1.25} sx={{ px: 1.35, pb: 1.25, pt: 0.25 }}>
              {showSourceDetail && runStats.statuses.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {[...runStats.withHits, ...runStats.withErr]
                    .slice(0, 24)
                    .map((s) => {
                      const label =
                        s.status === "no_scrapeable"
                          ? `${s.name}: SPA`
                          : s.status === "error"
                            ? `${s.name}: error`
                            : `${s.name}: ${s.candidates || 0}→${s.passed || 0}`;
                      return (
                        <Chip
                          key={`${s.id || s.name}-${s.status}`}
                          size="small"
                          color={
                            s.status === "error" || s.status === "no_scrapeable"
                              ? "warning"
                              : "success"
                          }
                          variant="outlined"
                          label={label}
                          sx={{
                            height: 22,
                            borderRadius: 1.25,
                            fontSize: "0.68rem",
                            "& .MuiChip-label": { px: 0.85 },
                          }}
                        />
                      );
                    })}
                </Stack>
              )}
              {showRuns &&
                (runsLoading && !runs.length ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("skillAgent.search.runsLoading")}
                  </Typography>
                ) : !runs.length ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("skillAgent.search.runsEmpty")}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {runs.slice(0, 8).map((run) => {
                      const snap =
                        run.ParamsSnapshot &&
                        typeof run.ParamsSnapshot === "object"
                          ? run.ParamsSnapshot
                          : {};
                      const tip = String(
                        snap.tipología || search.Tipologia || "",
                      );
                      const tipShort =
                        tip.length > 42 ? `${tip.slice(0, 42)}…` : tip || "—";
                      const when = run.createdAt
                        ? new Date(run.createdAt).toLocaleString("es-ES", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—";
                      return (
                        <Stack
                          key={run.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                            px: 1.1,
                            py: 0.45,
                            bgcolor: alpha("#fff", 0.7),
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ minWidth: 0 }}
                          >
                            {when} · +{run.ItemsAdded ?? 0} · {tipShort}
                          </Typography>
                          <Button
                            size="small"
                            disabled={locked || running}
                            onClick={() => handleRerun(run)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {t("skillAgent.search.rerun")}
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
                ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      <Box
        sx={{
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.primary.main, 0.1),
          borderRadius: 2.5,
          overflow: "hidden",
          bgcolor: alpha("#fff", 0.72),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          spacing={1}
          sx={{
            px: 1.35,
            py: 0.85,
            borderBottom: images.length ? "1px solid" : "none",
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.035),
          }}
        >
          <Typography variant="subtitle2" fontWeight={800}>
            {t("skillAgent.search.photosHeader", {
              n: images.length,
              sel: selectedCount,
            })}
          </Typography>
          {images.length > 0 && !running && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {selectedCount > 0 ? (
                <>
                  <Button
                    size="small"
                    onClick={clearSelection}
                    disabled={locked}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {t("skillAgent.search.clearSelection")}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    disabled={locked}
                    onClick={() => setSelectedDeleteOpen(true)}
                    startIcon={<DeleteOutlineIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    {t("skillAgent.search.deleteSelected", {
                      n: selectedCount,
                    })}
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  disabled={locked}
                  onClick={selectAllPhotos}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {t("skillAgent.search.selectAll")}
                </Button>
              )}
              <Button
                size="small"
                color="warning"
                onClick={() => setClearOpen(true)}
                disabled={locked}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {t("skillAgent.search.deleteAllPhotos")}
              </Button>
            </Stack>
          )}
        </Stack>

        <Box sx={{ p: 1.35 }}>
          {images.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("skillAgent.search.photosEmpty")}
            </Typography>
          ) : (
            (() => {
              const byBrand = new Map();
              for (const img of images) {
                const key =
                  img.BrandName ||
                  img.BrandId ||
                  t("skillAgent.search.noBrandGroup");
                if (!byBrand.has(key)) byBrand.set(key, []);
                byBrand.get(key).push(img);
              }
              const brandEntries = [...byBrand.entries()].sort((a, b) =>
                String(a[0]).localeCompare(String(b[0]), "es", {
                  sensitivity: "base",
                }),
              );
              return (
                <Stack spacing={1.75}>
                  {brandEntries.map(([brand, imgs]) => (
                    <Box key={brand}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ mb: 0.85 }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          color="text.secondary"
                          sx={{
                            letterSpacing: 0.04,
                            textTransform: "uppercase",
                          }}
                        >
                          {brand}
                        </Typography>
                        <Chip
                          size="small"
                          label={imgs.length}
                          sx={{
                            height: 18,
                            minWidth: 22,
                            borderRadius: 1,
                            fontWeight: 800,
                            fontSize: "0.65rem",
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                            "& .MuiChip-label": { px: 0.6 },
                          }}
                        />
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(132px, 1fr))",
                          gap: 1.1,
                        }}
                      >
                        {imgs.map((img) => (
                          <SearchThumb
                            key={img.id}
                            item={img}
                            selectable={!running}
                            selected={selectedIds.has(Number(img.id))}
                            onToggleSelect={toggleSelect}
                            onOpen={(it) => {
                              const idx = images.findIndex(
                                (x) => Number(x.id) === Number(it.id),
                              );
                              if (idx >= 0) setLightboxIndex(idx);
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              );
            })()
          )}
        </Box>
      </Box>

      {/* Edit params */}
      <Dialog
        open={editOpen}
        onClose={() => !busy && setEditOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.search.editDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {[search.CustomerName, search.Temporada]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              value={editForm.Titulo}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, Titulo: e.target.value }))
              }
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.form.tipologyKeywords")}
              value={editForm.Tipologia}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, Tipologia: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
              helperText={t("skillAgent.form.tipologyHelper")}
              sx={pageFieldSx}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                label={t("skillAgent.form.imgsPerBrand")}
                type="number"
                inputProps={{ min: 1, max: 12 }}
                value={editForm.maxImagesPerBrand}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxImagesPerBrand: e.target.value,
                  }))
                }
                sx={{ width: 120, ...pageFieldSx }}
              />
              <TextField
                size="small"
                label={t("skillAgent.form.maxAgeDays")}
                type="number"
                inputProps={{ min: 7, max: 365 }}
                value={editForm.maxAgeDays}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    maxAgeDays: e.target.value,
                  }))
                }
                helperText={t("skillAgent.form.maxAgeDaysHelper")}
                sx={{ width: 150, ...pageFieldSx }}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight={800}>
                {t("skillAgent.search.clientBrands")}
              </Typography>
              <Button
                size="small"
                sx={{ textTransform: "none", fontWeight: 700 }}
                disabled={!search?.CustomerId}
                onClick={() => setCatalogOpen(true)}
              >
                {t("skillAgent.search.manageCatalog")}
              </Button>
            </Stack>
            <SkillAgentBrandSourcesFields
              brands={catalogBrands}
              brandIds={editForm.brandIds}
              urlSelection={editForm.urlSelection}
              onBrandIdsChange={(brandIds) =>
                setEditForm((p) => ({ ...p, brandIds }))
              }
              onUrlSelectionChange={(urlSelection) =>
                setEditForm((p) => ({ ...p, urlSelection }))
              }
            />
            <SkillAgentCustomSourcesFields
              sources={editForm.customSources}
              onChange={(customSources) =>
                setEditForm((p) => ({ ...p, customSources }))
              }
            />
            <Typography variant="caption" color="text.secondary">
              Novedades (días) solo aplica si la web da fecha (p. ej. Shopify).
              Las webs one-off van abajo; lo estable mejor en el catálogo.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} disabled={busy}>
            {t("skillAgent.common.close")}
          </Button>
          <Button
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: false })}
          >
            {t("skillAgent.common.save")}
          </Button>
          <Button
            variant="contained"
            sx={pagePrimaryButtonSx}
            disabled={busy}
            onClick={() => handleSaveParams({ andRun: true })}
          >
            {t("skillAgent.search.saveAndRerun")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear all photos confirm */}
      <Dialog
        open={clearOpen}
        onClose={() => !busy && setClearOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.search.clearDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("skillAgent.search.clearDialogBody")}
          </Typography>
          {images.length > 0 ? (
            <Typography variant="body2" sx={{ mt: 1.25 }} fontWeight={700}>
              {t("skillAgent.search.clearDialogCount", { n: images.length })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearOpen(false)} disabled={busy}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={handleClearPhotos}
            startIcon={
              busy ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {t("skillAgent.search.deleteAllPhotos")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete selected photos */}
      <Dialog
        open={selectedDeleteOpen}
        onClose={() => !busy && setSelectedDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.search.deleteSelectedDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("skillAgent.search.deleteSelectedBody")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.25 }} fontWeight={700}>
            {t("skillAgent.search.clearDialogCount", { n: selectedCount })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedDeleteOpen(false)} disabled={busy}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || selectedCount === 0}
            onClick={handleDeleteSelected}
            startIcon={
              busy ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
          >
            {t("skillAgent.search.deleteSelected", { n: selectedCount })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.search.deleteDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("skillAgent.search.deleteDialogBody")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            {t("skillAgent.search.deleteSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <SearchPhotoLightbox
        items={images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        onDeleteCurrent={running ? undefined : handleDeleteLightboxItem}
        deleting={busy}
      />

      <SkillAgentResearchCatalogDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        customerId={search?.CustomerId}
        customerName={search?.CustomerName}
        onChanged={() => {
          const customerId = search?.CustomerId;
          getSkillAgentSearchesMeta(
            customerId ? { customerId: Number(customerId) } : {},
          )
            .then((m) => {
              const list = Array.isArray(m?.brands) ? m.brands : [];
              setCatalogBrands(
                [...list].sort((a, b) =>
                  String(a.name || a.id).localeCompare(
                    String(b.name || b.id),
                    "es",
                    { sensitivity: "base" },
                  ),
                ),
              );
            })
            .catch(() => {});
        }}
      />
    </Stack>
  );
}
